import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Grid,
  Inline,
  KpiCard,
  MoneyField,
  PageHeader,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  Switch,
  Tab,
  Tabs,
  TextAreaField,
  formatMoneyMinor,
  useToast
} from "@leylion/design-system";
import {
  approvePurchaseOrder,
  getBranches,
  getGoodsReceipts,
  getInventoryOverview,
  getProducts,
  getPurchaseOrder,
  getPurchaseOrders,
  getReorderRules,
  getRuntimeSettings,
  getStockBalances,
  getStoredContext,
  getSuppliers,
  getWarehouses,
  postInventoryAdjustment,
  postInventoryCount,
  postInventoryTransfer,
  receiveGoods,
  savePurchaseOrder,
  saveReorderRule,
  saveSupplier,
  saveWarehouse
} from "../api/client";
import type { Product } from "../api/catalog-types";
import type {
  GoodsReceipt,
  InventoryOverview,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderLineInput,
  ReorderRule,
  StockBalance,
  Supplier,
  Warehouse
} from "../api/inventory-types";
import type { Branch } from "../api/settings-types";
import "./inventory.css";

type Language = "ar" | "en";
type InventoryTab = "overview" | "stock" | "warehouses" | "suppliers" | "orders" | "receipts";
type StockOperation = "adjustment" | "count" | "transfer";

type DraftLine = {
  productVariantId: string;
  quantityMilli: number;
  unitCostMinor: number;
  taxRateBasisPoints: number;
};

type ReceiptDraft = {
  purchaseOrderId: string;
  warehouseId: string;
  supplierId: string;
  supplierInvoiceNumber: string;
  lines: DraftLine[];
};

const quantityToMilli = (raw: string): number | null => {
  const normalized = Number(raw);
  if (!Number.isFinite(normalized)) return null;
  const milli = Math.round(normalized * 1000);
  return Number.isSafeInteger(milli) ? milli : null;
};

const milliToQuantity = (milli: number): string => {
  const value = milli / 1000;
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
};

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function InventoryPurchasingCenter({
  language,
  permissions
}: {
  language: Language;
  permissions: string[];
}) {
  const toast = useToast();
  const ar = language === "ar";
  const can = (permission: string) => permissions.includes(permission);
  const canViewCost = can("inventory.cost.read");
  const canAdjust = can("inventory.adjust");
  const canCount = can("inventory.count");
  const canTransfer = can("inventory.transfer");
  const canManageWarehouses = can("branches.manage");
  const canManagePurchasing = can("purchasing.manage");
  const canApprovePurchasing = can("purchasing.approve");
  const canReceive = can("receiving.manage");

  const [tab, setTab] = useState<InventoryTab>("overview");
  const [overview, setOverview] = useState<InventoryOverview | null>(null);
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [rules, setRules] = useState<ReorderRule[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [currency, setCurrency] = useState("AED");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [stockOperation, setStockOperation] = useState<StockOperation | null>(null);
  const [stockWarehouseId, setStockWarehouseId] = useState("");
  const [stockDestinationId, setStockDestinationId] = useState("");
  const [stockVariantId, setStockVariantId] = useState("");
  const [stockQuantity, setStockQuantity] = useState("1");
  const [stockUnitCostMinor, setStockUnitCostMinor] = useState<number | null>(0);
  const [stockReason, setStockReason] = useState("");

  const [warehouseEditor, setWarehouseEditor] = useState<Partial<Warehouse> | null>(null);
  const [ruleEditor, setRuleEditor] = useState<Partial<ReorderRule> | null>(null);
  const [supplierEditor, setSupplierEditor] = useState<Partial<Supplier> | null>(null);
  const [purchaseOrderEditor, setPurchaseOrderEditor] = useState<PurchaseOrderDetail | null | "new">(null);
  const [poWarehouseId, setPoWarehouseId] = useState("");
  const [poSupplierId, setPoSupplierId] = useState("");
  const [poExpectedAt, setPoExpectedAt] = useState("");
  const [poLines, setPoLines] = useState<DraftLine[]>([]);
  const [receiptEditor, setReceiptEditor] = useState<ReceiptDraft | null>(null);

  const variants = useMemo(
    () => products.flatMap((product) => product.variants.map((variant) => ({ product, variant }))),
    [products]
  );
  const context = getStoredContext();

  const money = (minor: number, valueCurrency = currency) =>
    formatMoneyMinor(minor, valueCurrency, ar ? "ar-AE" : "en-AE");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const branchId = context?.branchId ?? "";
      const [
        overviewData,
        balanceData,
        warehouseData,
        ruleData,
        supplierData,
        poData,
        receiptData,
        productData,
        branchData,
        runtime
      ] = await Promise.all([
        getInventoryOverview(),
        getStockBalances(),
        getWarehouses(),
        getReorderRules(),
        getSuppliers(),
        getPurchaseOrders(),
        getGoodsReceipts(),
        getProducts(),
        canManageWarehouses ? getBranches().catch(() => []) : Promise.resolve([]),
        branchId ? getRuntimeSettings(branchId).catch(() => null) : Promise.resolve(null)
      ]);
      setOverview(overviewData);
      setBalances(balanceData);
      setWarehouses(warehouseData);
      setRules(ruleData);
      setSuppliers(supplierData);
      setPurchaseOrders(poData);
      setReceipts(receiptData);
      setProducts(productData);
      setBranches(branchData);
      if (runtime?.currency) setCurrency(runtime.currency);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const defaultWarehouse = warehouses.find((item) => item.branchId === context?.branchId && item.isDefault)
    ?? warehouses.find((item) => item.branchId === context?.branchId)
    ?? warehouses[0];

  const openStockOperation = (operation: StockOperation, row?: StockBalance) => {
    setStockOperation(operation);
    setStockWarehouseId(row?.warehouseId ?? defaultWarehouse?.id ?? "");
    setStockDestinationId(warehouses.find((item) => item.id !== (row?.warehouseId ?? defaultWarehouse?.id))?.id ?? "");
    setStockVariantId(row?.productVariantId ?? variants[0]?.variant.id ?? "");
    setStockQuantity(operation === "count" && row ? milliToQuantity(row.quantityMilli) : "1");
    setStockUnitCostMinor(row?.averageCostMinor ?? 0);
    setStockReason(operation === "count" ? (ar ? "جرد فعلي" : "Physical count") : "");
  };

  const submitStockOperation = async () => {
    if (!stockOperation) return;
    const quantityMilli = quantityToMilli(stockQuantity);
    if (!stockWarehouseId || !stockVariantId || quantityMilli == null) {
      setError(ar ? "أكمل بيانات حركة المخزون." : "Complete the inventory operation fields.");
      return;
    }
    try {
      if (stockOperation === "adjustment") {
        if (quantityMilli === 0 || !stockReason.trim()) throw new Error(ar ? "الكمية والسبب مطلوبان." : "Quantity and reason are required.");
        await postInventoryAdjustment({
          warehouseId: stockWarehouseId,
          productVariantId: stockVariantId,
          quantityMilli,
          unitCostMinor: stockUnitCostMinor ?? 0,
          reason: stockReason.trim()
        });
      } else if (stockOperation === "count") {
        if (quantityMilli < 0 || !stockReason.trim()) throw new Error(ar ? "الكمية المعدودة والسبب مطلوبان." : "Counted quantity and reason are required.");
        await postInventoryCount({
          warehouseId: stockWarehouseId,
          productVariantId: stockVariantId,
          quantityMilli,
          unitCostMinor: stockUnitCostMinor ?? 0,
          reason: stockReason.trim()
        });
      } else {
        if (!stockDestinationId || stockDestinationId === stockWarehouseId || quantityMilli <= 0) {
          throw new Error(ar ? "اختر مستودعين مختلفين وكمية موجبة." : "Choose different warehouses and a positive quantity.");
        }
        await postInventoryTransfer({
          sourceWarehouseId: stockWarehouseId,
          destinationWarehouseId: stockDestinationId,
          productVariantId: stockVariantId,
          quantityMilli
        });
      }
      setStockOperation(null);
      toast.notify({ tone: "success", title: ar ? "تم ترحيل حركة المخزون" : "Inventory operation posted" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const saveWarehouseEditor = async () => {
    if (!warehouseEditor) return;
    try {
      await saveWarehouse({
        ...warehouseEditor,
        branchId: warehouseEditor.branchId ?? context?.branchId ?? branches[0]?.id ?? "",
        code: warehouseEditor.code ?? "",
        nameAr: warehouseEditor.nameAr ?? "",
        nameEn: warehouseEditor.nameEn ?? ""
      });
      setWarehouseEditor(null);
      toast.notify({ tone: "success", title: ar ? "تم حفظ المستودع" : "Warehouse saved" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const saveRuleEditor = async () => {
    if (!ruleEditor) return;
    try {
      await saveReorderRule({
        ...ruleEditor,
        warehouseId: ruleEditor.warehouseId ?? defaultWarehouse?.id ?? "",
        productVariantId: ruleEditor.productVariantId ?? variants[0]?.variant.id ?? "",
        minimumQuantityMilli: ruleEditor.minimumQuantityMilli ?? 0,
        reorderQuantityMilli: ruleEditor.reorderQuantityMilli ?? 1000,
        maximumQuantityMilli: ruleEditor.maximumQuantityMilli ?? 10000
      });
      setRuleEditor(null);
      toast.notify({ tone: "success", title: ar ? "تم حفظ قاعدة إعادة الطلب" : "Reorder rule saved" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const saveSupplierEditor = async () => {
    if (!supplierEditor) return;
    try {
      await saveSupplier({
        ...supplierEditor,
        legalName: supplierEditor.legalName ?? "",
        currency: supplierEditor.currency ?? currency
      });
      setSupplierEditor(null);
      toast.notify({ tone: "success", title: ar ? "تم حفظ المورد" : "Supplier saved" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const newDraftLine = (): DraftLine => ({
    productVariantId: variants[0]?.variant.id ?? "",
    quantityMilli: 1000,
    unitCostMinor: 0,
    taxRateBasisPoints: 500
  });

  const openNewPurchaseOrder = () => {
    const warehouse = defaultWarehouse;
    setPurchaseOrderEditor("new");
    setPoWarehouseId(warehouse?.id ?? "");
    setPoSupplierId(suppliers.find((item) => item.status === "Active")?.id ?? suppliers[0]?.id ?? "");
    setPoExpectedAt("");
    setPoLines([newDraftLine()]);
  };

  const openPurchaseOrder = async (row: PurchaseOrder) => {
    try {
      const detail = await getPurchaseOrder(row.id);
      setPurchaseOrderEditor(detail);
      setPoWarehouseId(detail.warehouseId);
      setPoSupplierId(detail.supplierId);
      setPoExpectedAt(detail.expectedAt ? detail.expectedAt.slice(0, 10) : "");
      setPoLines(detail.lines.map((line) => ({
        productVariantId: line.productVariantId,
        quantityMilli: line.orderedQuantityMilli,
        unitCostMinor: line.unitCostMinor,
        taxRateBasisPoints: line.taxRateBasisPoints
      })));
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const savePurchaseOrderEditor = async () => {
    if (!purchaseOrderEditor || poLines.length === 0) return;
    const warehouse = warehouses.find((item) => item.id === poWarehouseId);
    if (!warehouse || !poSupplierId || poLines.some((line) => !line.productVariantId || line.quantityMilli <= 0)) {
      setError(ar ? "أكمل بيانات أمر الشراء." : "Complete the purchase order fields.");
      return;
    }
    try {
      await savePurchaseOrder({
        id: purchaseOrderEditor === "new" ? undefined : purchaseOrderEditor.id,
        branchId: warehouse.branchId,
        warehouseId: warehouse.id,
        supplierId: poSupplierId,
        currency: suppliers.find((item) => item.id === poSupplierId)?.currency ?? currency,
        expectedAt: poExpectedAt ? new Date(`${poExpectedAt}T12:00:00`).toISOString() : null,
        lines: poLines,
        version: purchaseOrderEditor === "new" ? undefined : purchaseOrderEditor.version
      });
      setPurchaseOrderEditor(null);
      toast.notify({ tone: "success", title: ar ? "تم حفظ أمر الشراء" : "Purchase order saved" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const approve = async (row: PurchaseOrder) => {
    try {
      await approvePurchaseOrder(row.id, row.version);
      toast.notify({ tone: "success", title: ar ? "تم اعتماد أمر الشراء" : "Purchase order approved" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const openReceipt = async (po?: PurchaseOrder) => {
    if (!po) {
      setReceiptEditor({
        purchaseOrderId: "",
        warehouseId: defaultWarehouse?.id ?? "",
        supplierId: suppliers.find((item) => item.status === "Active")?.id ?? suppliers[0]?.id ?? "",
        supplierInvoiceNumber: `UAT-${Date.now()}`,
        lines: [newDraftLine()]
      });
      return;
    }
    try {
      const detail = await getPurchaseOrder(po.id);
      setReceiptEditor({
        purchaseOrderId: detail.id,
        warehouseId: detail.warehouseId,
        supplierId: detail.supplierId,
        supplierInvoiceNumber: `INV-${Date.now()}`,
        lines: detail.lines
          .filter((line) => line.receivedQuantityMilli < line.orderedQuantityMilli)
          .map((line) => ({
            productVariantId: line.productVariantId,
            quantityMilli: line.orderedQuantityMilli - line.receivedQuantityMilli,
            unitCostMinor: line.unitCostMinor,
            taxRateBasisPoints: line.taxRateBasisPoints
          }))
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const submitReceipt = async () => {
    if (!receiptEditor || !receiptEditor.supplierInvoiceNumber.trim() || receiptEditor.lines.length === 0) return;
    try {
      await receiveGoods({
        warehouseId: receiptEditor.warehouseId,
        supplierId: receiptEditor.supplierId,
        purchaseOrderId: receiptEditor.purchaseOrderId || null,
        supplierInvoiceNumber: receiptEditor.supplierInvoiceNumber.trim(),
        lines: receiptEditor.lines
      });
      setReceiptEditor(null);
      toast.notify({ tone: "success", title: ar ? "تم تسجيل استلام البضاعة" : "Goods receipt posted" });
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  if (loading) {
    return <Card><Inline><Spinner label={ar ? "جارٍ التحميل" : "Loading"} /><span>{ar ? "جارٍ تحميل سلسلة التوريد…" : "Loading supply-chain operations…"}</span></Inline></Card>;
  }

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "إدارة سلسلة التوريد" : "Supply Chain"}
        title={ar ? "المخزون والمشتريات" : "Inventory & Purchasing"}
        description={ar ? "إدارة المستودعات والأرصدة والجرد والتحويلات وإعادة الطلب والموردين وأوامر الشراء والاستلام." : "Manage warehouses, balances, counts, transfers, reorder rules, suppliers, purchase orders and receiving."}
      />

      {error ? <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Operation failed"}>{error}</Alert> : null}

      <Tabs value={tab} onValueChange={(value) => setTab(value as InventoryTab)} label="inventory">
        <Tab value="overview">{ar ? "نظرة عامة" : "Overview"}</Tab>
        <Tab value="stock">{ar ? "الأرصدة والحركات" : "Stock & movements"}</Tab>
        <Tab value="warehouses">{ar ? "المستودعات وإعادة الطلب" : "Warehouses & reorder"}</Tab>
        <Tab value="suppliers">{ar ? "الموردون" : "Suppliers"}</Tab>
        <Tab value="orders">{ar ? "أوامر الشراء" : "Purchase Orders"}</Tab>
        <Tab value="receipts">{ar ? "الاستلام" : "Receiving"}</Tab>
      </Tabs>

      {tab === "overview" && overview ? (
        <Stack>
          <Grid columns={4}>
            <KpiCard label={ar ? "المستودعات" : "Warehouses"} value={overview.warehouses} />
            <KpiCard label={ar ? "الأصناف المتتبعة" : "Tracked variants"} value={overview.trackedVariants} />
            <KpiCard label={ar ? "منخفض المخزون" : "Low stock"} value={overview.lowStockVariants} trendTone={overview.lowStockVariants ? "warning" : "success"} />
            <KpiCard label={ar ? "نفد المخزون" : "Out of stock"} value={overview.outOfStockVariants} trendTone={overview.outOfStockVariants ? "danger" : "success"} />
            <KpiCard label={ar ? "قيمة المخزون" : "Stock value"} value={canViewCost && overview.stockValueMinor != null ? money(overview.stockValueMinor) : "—"} />
            <KpiCard label={ar ? "أوامر شراء مفتوحة" : "Open POs"} value={overview.openPurchaseOrders} />
            <KpiCard label={ar ? "موردون نشطون" : "Active suppliers"} value={overview.activeSuppliers} />
          </Grid>
          {overview.warnings.length ? <Alert tone="warning" title={ar ? "تنبيهات" : "Warnings"}>{overview.warnings.join(" · ")}</Alert> : <Alert tone="success" title={ar ? "المخزون بحالة جيدة" : "Inventory healthy"} />}
        </Stack>
      ) : null}

      {tab === "stock" ? (
        <Panel
          title={ar ? "الأرصدة الحالية" : "Current Stock"}
          actions={<Inline>
            {canAdjust ? <Button data-testid="inventory-adjust" size="sm" onClick={() => openStockOperation("adjustment")}>{ar ? "تسوية" : "Adjustment"}</Button> : null}
            {canCount ? <Button data-testid="inventory-count" size="sm" variant="secondary" onClick={() => openStockOperation("count")}>{ar ? "جرد" : "Count"}</Button> : null}
            {canTransfer ? <Button data-testid="inventory-transfer" size="sm" variant="secondary" onClick={() => openStockOperation("transfer")}>{ar ? "تحويل" : "Transfer"}</Button> : null}
          </Inline>}
        >
          {balances.length ? <DataTable rows={balances} rowKey={(row) => `${row.warehouseId}-${row.productVariantId}`} columns={[
            { key: "product", header: ar ? "الصنف" : "Item", cell: (row) => <button className="inventory-link" onClick={() => canAdjust && openStockOperation("adjustment", row)}><strong>{ar ? row.productNameAr : row.productNameEn}</strong><small>{row.sku} · {row.warehouseName}</small></button> },
            { key: "qty", header: ar ? "الكمية" : "Quantity", cell: (row) => milliToQuantity(row.quantityMilli) },
            { key: "reserved", header: ar ? "محجوز" : "Reserved", cell: (row) => milliToQuantity(row.reservedQuantityMilli) },
            { key: "available", header: ar ? "المتاح" : "Available", cell: (row) => milliToQuantity(row.availableQuantityMilli) },
            { key: "cost", header: ar ? "التكلفة" : "Cost", cell: (row) => canViewCost && row.averageCostMinor != null ? money(row.averageCostMinor) : "—" },
            { key: "value", header: ar ? "القيمة" : "Value", cell: (row) => canViewCost && row.stockValueMinor != null ? money(row.stockValueMinor) : "—" },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isLowStock ? "warning" : "success"}>{row.isLowStock ? (ar ? "منخفض" : "Low") : (ar ? "طبيعي" : "Healthy")}</StatusBadge> }
          ]} /> : <EmptyState title={ar ? "لا توجد أرصدة بعد" : "No stock balances yet"} description={ar ? "استخدم التسوية أو الاستلام لإنشاء أول رصيد." : "Use an adjustment or goods receipt to create the first balance."} />}
        </Panel>
      ) : null}

      {tab === "warehouses" ? (
        <Stack>
          <Panel data-testid="warehouses-panel" title={ar ? "المستودعات" : "Warehouses"} actions={canManageWarehouses ? <Button data-testid="new-warehouse" onClick={() => setWarehouseEditor({ branchId: context?.branchId ?? branches[0]?.id ?? "", code: "", nameAr: "", nameEn: "", isDefault: false, isActive: true })}>{ar ? "مستودع جديد" : "New warehouse"}</Button> : null}>
            <DataTable rows={warehouses} rowKey={(row) => row.id} columns={[
              { key: "name", header: ar ? "المستودع" : "Warehouse", cell: (row) => <button className="inventory-link" onClick={() => canManageWarehouses && setWarehouseEditor(row)}><strong>{ar ? row.nameAr : row.nameEn}</strong><small>{row.code} · {row.branchName}</small></button> },
              { key: "tracked", header: ar ? "أصناف متتبعة" : "Tracked", cell: (row) => row.trackedVariantCount },
              { key: "default", header: ar ? "الافتراضي" : "Default", cell: (row) => <StatusBadge tone={row.isDefault ? "info" : "neutral"}>{row.isDefault ? (ar ? "افتراضي" : "Default") : "—"}</StatusBadge> },
              { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? (ar ? "نشط" : "Active") : (ar ? "غير نشط" : "Inactive")}</StatusBadge> }
            ]} />
          </Panel>
          <Panel data-testid="reorder-rules-panel" title={ar ? "قواعد إعادة الطلب" : "Reorder Rules"} actions={canAdjust ? <Button data-testid="new-reorder-rule" onClick={() => setRuleEditor({ warehouseId: defaultWarehouse?.id ?? "", productVariantId: variants[0]?.variant.id ?? "", minimumQuantityMilli: 5000, reorderQuantityMilli: 10000, maximumQuantityMilli: 30000, isActive: true })}>{ar ? "قاعدة جديدة" : "New rule"}</Button> : null}>
            {rules.length ? <DataTable rows={rules} rowKey={(row) => row.id} columns={[
              { key: "item", header: ar ? "الصنف" : "Item", cell: (row) => <button className="inventory-link" onClick={() => canAdjust && setRuleEditor(row)}><strong>{ar ? row.productNameAr : row.productNameEn}</strong><small>{row.sku} · {row.warehouseName}</small></button> },
              { key: "min", header: ar ? "الحد الأدنى" : "Minimum", cell: (row) => milliToQuantity(row.minimumQuantityMilli) },
              { key: "reorder", header: ar ? "كمية الطلب" : "Reorder qty", cell: (row) => milliToQuantity(row.reorderQuantityMilli) },
              { key: "max", header: ar ? "الحد الأعلى" : "Maximum", cell: (row) => milliToQuantity(row.maximumQuantityMilli) },
              { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? (ar ? "نشط" : "Active") : (ar ? "متوقف" : "Inactive")}</StatusBadge> }
            ]} /> : <EmptyState title={ar ? "لا توجد قواعد إعادة طلب" : "No reorder rules"} description={ar ? "أضف حدود الحد الأدنى وكمية إعادة الطلب لكل صنف ومستودع." : "Define minimum and replenishment quantities per item and warehouse."} />}
          </Panel>
        </Stack>
      ) : null}

      {tab === "suppliers" ? (
        <Panel data-testid="suppliers-panel" title={ar ? "الموردون" : "Suppliers"} actions={canManagePurchasing ? <Button data-testid="new-supplier" onClick={() => setSupplierEditor({ legalName: "", tradeName: null, phone: null, email: null, currency, paymentTermsDays: 30, status: "Active" })}>{ar ? "مورد جديد" : "New supplier"}</Button> : null}>
          {suppliers.length ? <DataTable rows={suppliers} rowKey={(row) => row.id} columns={[
            { key: "name", header: ar ? "المورد" : "Supplier", cell: (row) => <button className="inventory-link" onClick={() => canManagePurchasing && setSupplierEditor(row)}><strong>{row.tradeName ?? row.legalName}</strong><small>{row.supplierNumber}</small></button> },
            { key: "terms", header: ar ? "شروط الدفع" : "Terms", cell: (row) => `${row.paymentTermsDays} ${ar ? "يوم" : "days"}` },
            { key: "open", header: ar ? "أوامر مفتوحة" : "Open POs", cell: (row) => row.openPurchaseOrders },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : "warning"}>{row.status}</StatusBadge> }
          ]} /> : <EmptyState title={ar ? "لا يوجد موردون" : "No suppliers"} description={ar ? "أضف موردًا لبدء دورة الشراء." : "Add a supplier to start purchasing."} />}
        </Panel>
      ) : null}

      {tab === "orders" ? (
        <Panel data-testid="purchase-orders-panel" title={ar ? "أوامر الشراء" : "Purchase Orders"} actions={canManagePurchasing ? <Button data-testid="new-purchase-order" onClick={openNewPurchaseOrder}>{ar ? "أمر شراء جديد" : "New purchase order"}</Button> : null}>
          {purchaseOrders.length ? <DataTable rows={purchaseOrders} rowKey={(row) => row.id} columns={[
            { key: "number", header: ar ? "الأمر" : "PO", cell: (row) => <button className="inventory-link" onClick={() => row.status === "Draft" && canManagePurchasing ? void openPurchaseOrder(row) : undefined}><strong>{row.purchaseOrderNumber}</strong><small>{row.supplierName} · {row.warehouseName}</small></button> },
            { key: "total", header: ar ? "الإجمالي" : "Total", cell: (row) => money(row.totalMinor, row.currency) },
            { key: "lines", header: ar ? "البنود" : "Lines", cell: (row) => row.lineCount },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Received" ? "success" : row.status === "Draft" ? "neutral" : "info"}>{row.status}</StatusBadge> },
            { key: "action", header: "", cell: (row) => <Inline>{row.status === "Draft" && canApprovePurchasing ? <Button size="sm" onClick={() => void approve(row)}>{ar ? "اعتماد" : "Approve"}</Button> : null}{(row.status === "Approved" || row.status === "PartiallyReceived") && canReceive ? <Button size="sm" variant="secondary" onClick={() => void openReceipt(row)}>{ar ? "استلام" : "Receive"}</Button> : null}</Inline> }
          ]} /> : <EmptyState title={ar ? "لا توجد أوامر شراء" : "No purchase orders"} description={ar ? "أنشئ أمر شراء وأرسله للاعتماد ثم الاستلام." : "Create a purchase order, approve it, then receive goods."} />}
        </Panel>
      ) : null}

      {tab === "receipts" ? (
        <Panel data-testid="goods-receipts-panel" title={ar ? "استلام البضاعة" : "Goods Receipts"} actions={canReceive ? <Button data-testid="new-goods-receipt" onClick={() => void openReceipt()}>{ar ? "استلام مباشر" : "Direct receipt"}</Button> : null}>
          {receipts.length ? <DataTable rows={receipts} rowKey={(row) => row.id} columns={[
            { key: "number", header: ar ? "السند" : "Receipt", cell: (row) => <span><strong>{row.receiptNumber}</strong><small>{row.supplierInvoiceNumber}</small></span> },
            { key: "supplier", header: ar ? "المورد" : "Supplier", cell: (row) => row.supplierName },
            { key: "warehouse", header: ar ? "المستودع" : "Warehouse", cell: (row) => row.warehouseName },
            { key: "cost", header: ar ? "التكلفة" : "Cost", cell: (row) => canViewCost ? money(row.totalCostMinor) : "—" },
            { key: "date", header: ar ? "التاريخ" : "Date", cell: (row) => new Date(row.receivedAt).toLocaleString(ar ? "ar-AE" : "en-AE") }
          ]} /> : <EmptyState title={ar ? "لا توجد سندات استلام" : "No goods receipts"} description={ar ? "يمكن الاستلام مباشرة أو مقابل أمر شراء معتمد." : "Receive directly or against an approved purchase order."} />}
        </Panel>
      ) : null}

      <Dialog open={Boolean(stockOperation)} onClose={() => setStockOperation(null)} title={stockOperation === "adjustment" ? (ar ? "تسوية المخزون" : "Stock adjustment") : stockOperation === "count" ? (ar ? "الجرد الفعلي" : "Physical count") : (ar ? "تحويل مخزون" : "Stock transfer")} footer={<Inline><Button variant="secondary" onClick={() => setStockOperation(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="submit-stock-operation" onClick={() => void submitStockOperation()}>{ar ? "ترحيل" : "Post"}</Button></Inline>}>
        {stockOperation ? <Stack><Grid columns={2}>
          <SelectField data-testid="stock-operation-source-warehouse" label={ar ? "المستودع" : "Warehouse"} value={stockWarehouseId} onChange={(event) => setStockWarehouseId(event.target.value)}>{warehouses.filter((item) => item.isActive).map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn} · {item.branchName}</option>)}</SelectField>
          <SelectField label={ar ? "الصنف" : "Item"} value={stockVariantId} onChange={(event) => setStockVariantId(event.target.value)}>{variants.map(({ product, variant }) => <option key={variant.id} value={variant.id}>{ar ? product.nameAr : product.nameEn} · {variant.sku}</option>)}</SelectField>
          {stockOperation === "transfer" ? <SelectField data-testid="stock-operation-destination-warehouse" label={ar ? "المستودع المستلم" : "Destination warehouse"} value={stockDestinationId} onChange={(event) => setStockDestinationId(event.target.value)}>{warehouses.filter((item) => item.isActive && item.id !== stockWarehouseId).map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn} · {item.branchName}</option>)}</SelectField> : null}
          <Field data-testid="stock-operation-quantity" label={stockOperation === "count" ? (ar ? "الكمية المعدودة" : "Counted quantity") : (ar ? "الكمية" : "Quantity")} type="number" step="0.001" value={stockQuantity} onChange={(event) => setStockQuantity(event.target.value)} hint={stockOperation === "adjustment" ? (ar ? "استخدم قيمة سالبة للنقص." : "Use a negative value to decrease stock.") : undefined} />
          {stockOperation === "adjustment" && canViewCost ? <MoneyField label={ar ? "تكلفة الوحدة" : "Unit cost"} valueMinor={stockUnitCostMinor} currency={currency} onMinorChange={setStockUnitCostMinor} /> : null}
        </Grid>{stockOperation !== "transfer" ? <TextAreaField label={ar ? "السبب" : "Reason"} value={stockReason} onChange={(event) => setStockReason(event.target.value)} /> : null}</Stack> : null}
      </Dialog>

      <Dialog open={Boolean(warehouseEditor)} onClose={() => setWarehouseEditor(null)} title={ar ? "المستودع" : "Warehouse"} footer={<Inline><Button variant="secondary" onClick={() => setWarehouseEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-warehouse" onClick={() => void saveWarehouseEditor()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {warehouseEditor ? <Grid columns={2}>
          <SelectField label={ar ? "الفرع" : "Branch"} value={warehouseEditor.branchId ?? ""} disabled={Boolean(warehouseEditor.id)} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, branchId: event.target.value })}>{branches.length ? branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>) : context?.branchId ? <option value={context.branchId}>{context.branchName}</option> : null}</SelectField>
          <Field label={ar ? "الرمز" : "Code"} value={warehouseEditor.code ?? ""} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, code: event.target.value.toUpperCase() })} />
          <Field label={ar ? "الاسم العربي" : "Arabic name"} value={warehouseEditor.nameAr ?? ""} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, nameAr: event.target.value })} />
          <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={warehouseEditor.nameEn ?? ""} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, nameEn: event.target.value })} />
          <Switch label={ar ? "المستودع الافتراضي" : "Default warehouse"} checked={warehouseEditor.isDefault ?? false} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, isDefault: event.target.checked })} />
          <Switch label={ar ? "نشط" : "Active"} checked={warehouseEditor.isActive ?? true} onChange={(event) => setWarehouseEditor({ ...warehouseEditor, isActive: event.target.checked })} />
        </Grid> : null}
      </Dialog>

      <Dialog open={Boolean(ruleEditor)} onClose={() => setRuleEditor(null)} title={ar ? "قاعدة إعادة الطلب" : "Reorder rule"} footer={<Inline><Button variant="secondary" onClick={() => setRuleEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-reorder-rule" onClick={() => void saveRuleEditor()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {ruleEditor ? <Grid columns={2}>
          <SelectField data-testid="reorder-rule-warehouse" label={ar ? "المستودع" : "Warehouse"} value={ruleEditor.warehouseId ?? ""} onChange={(event) => setRuleEditor({ ...ruleEditor, warehouseId: event.target.value })}>{warehouses.filter((item) => item.isActive).map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}</SelectField>
          <SelectField label={ar ? "الصنف" : "Item"} value={ruleEditor.productVariantId ?? ""} onChange={(event) => setRuleEditor({ ...ruleEditor, productVariantId: event.target.value })}>{variants.map(({ product, variant }) => <option key={variant.id} value={variant.id}>{ar ? product.nameAr : product.nameEn} · {variant.sku}</option>)}</SelectField>
          <Field label={ar ? "الحد الأدنى" : "Minimum"} type="number" step="0.001" value={milliToQuantity(ruleEditor.minimumQuantityMilli ?? 0)} onChange={(event) => setRuleEditor({ ...ruleEditor, minimumQuantityMilli: quantityToMilli(event.target.value) ?? 0 })} />
          <Field label={ar ? "كمية إعادة الطلب" : "Reorder quantity"} type="number" step="0.001" value={milliToQuantity(ruleEditor.reorderQuantityMilli ?? 1000)} onChange={(event) => setRuleEditor({ ...ruleEditor, reorderQuantityMilli: quantityToMilli(event.target.value) ?? 0 })} />
          <Field label={ar ? "الحد الأعلى" : "Maximum"} type="number" step="0.001" value={milliToQuantity(ruleEditor.maximumQuantityMilli ?? 10000)} onChange={(event) => setRuleEditor({ ...ruleEditor, maximumQuantityMilli: quantityToMilli(event.target.value) ?? 0 })} />
          <Switch label={ar ? "نشطة" : "Active"} checked={ruleEditor.isActive ?? true} onChange={(event) => setRuleEditor({ ...ruleEditor, isActive: event.target.checked })} />
        </Grid> : null}
      </Dialog>

      <Dialog open={Boolean(supplierEditor)} onClose={() => setSupplierEditor(null)} title={ar ? "المورد" : "Supplier"} footer={<Inline><Button variant="secondary" onClick={() => setSupplierEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-supplier" onClick={() => void saveSupplierEditor()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {supplierEditor ? <Grid columns={2}>
          <Field label={ar ? "الاسم القانوني" : "Legal name"} value={supplierEditor.legalName ?? ""} onChange={(event) => setSupplierEditor({ ...supplierEditor, legalName: event.target.value })} />
          <Field label={ar ? "الاسم التجاري" : "Trade name"} value={supplierEditor.tradeName ?? ""} onChange={(event) => setSupplierEditor({ ...supplierEditor, tradeName: event.target.value || null })} />
          <Field label={ar ? "الهاتف" : "Phone"} value={supplierEditor.phone ?? ""} onChange={(event) => setSupplierEditor({ ...supplierEditor, phone: event.target.value || null })} />
          <Field label={ar ? "البريد" : "Email"} type="email" value={supplierEditor.email ?? ""} onChange={(event) => setSupplierEditor({ ...supplierEditor, email: event.target.value || null })} />
          <Field label={ar ? "العملة" : "Currency"} value={supplierEditor.currency ?? currency} maxLength={3} onChange={(event) => setSupplierEditor({ ...supplierEditor, currency: event.target.value.toUpperCase() })} />
          <Field label={ar ? "شروط الدفع بالأيام" : "Payment terms days"} type="number" min={0} max={365} value={supplierEditor.paymentTermsDays ?? 0} onChange={(event) => setSupplierEditor({ ...supplierEditor, paymentTermsDays: Number(event.target.value) })} />
          <SelectField label={ar ? "الحالة" : "Status"} value={supplierEditor.status ?? "Active"} onChange={(event) => setSupplierEditor({ ...supplierEditor, status: event.target.value as Supplier["status"] })}>{["Active", "OnHold", "Inactive"].map((value) => <option key={value}>{value}</option>)}</SelectField>
        </Grid> : null}
      </Dialog>

      <Dialog open={Boolean(purchaseOrderEditor)} onClose={() => setPurchaseOrderEditor(null)} title={ar ? "أمر الشراء" : "Purchase order"} footer={<Inline><Button variant="secondary" onClick={() => setPurchaseOrderEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-purchase-order" onClick={() => void savePurchaseOrderEditor()}>{ar ? "حفظ المسودة" : "Save draft"}</Button></Inline>}>
        {purchaseOrderEditor ? <Stack>
          <Grid columns={3}>
            <SelectField label={ar ? "المستودع" : "Warehouse"} value={poWarehouseId} onChange={(event) => setPoWarehouseId(event.target.value)}>{warehouses.filter((item) => item.isActive && item.branchId === context?.branchId).map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}</SelectField>
            <SelectField data-testid="purchase-order-supplier" label={ar ? "المورد" : "Supplier"} value={poSupplierId} onChange={(event) => setPoSupplierId(event.target.value)}>{suppliers.filter((item) => item.status === "Active").map((item) => <option key={item.id} value={item.id}>{item.tradeName ?? item.legalName}</option>)}</SelectField>
            <Field label={ar ? "تاريخ التوريد المتوقع" : "Expected delivery"} type="date" value={poExpectedAt} onChange={(event) => setPoExpectedAt(event.target.value)} />
          </Grid>
          <Card title={ar ? "بنود أمر الشراء" : "Purchase order lines"} actions={<Button size="sm" variant="secondary" onClick={() => setPoLines((current) => [...current, newDraftLine()])}>{ar ? "إضافة بند" : "Add line"}</Button>}>
            <Stack>{poLines.map((line, index) => <Grid columns={4} key={`${line.productVariantId}-${index}`}>
              <SelectField label={ar ? "الصنف" : "Item"} value={line.productVariantId} onChange={(event) => setPoLines((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, productVariantId: event.target.value } : item))}>{variants.map(({ product, variant }) => <option key={variant.id} value={variant.id}>{ar ? product.nameAr : product.nameEn} · {variant.sku}</option>)}</SelectField>
              <Field label={ar ? "الكمية" : "Quantity"} type="number" step="0.001" min="0.001" value={milliToQuantity(line.quantityMilli)} onChange={(event) => setPoLines((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, quantityMilli: quantityToMilli(event.target.value) ?? 0 } : item))} />
              <MoneyField data-testid={`purchase-order-unit-cost-${index}`} label={ar ? "تكلفة الوحدة" : "Unit cost"} valueMinor={line.unitCostMinor} currency={suppliers.find((item) => item.id === poSupplierId)?.currency ?? currency} onMinorChange={(value) => setPoLines((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, unitCostMinor: value ?? 0 } : item))} />
              <Field label={ar ? "ضريبة القيمة %" : "VAT %"} type="number" step="0.01" min={0} max={100} value={line.taxRateBasisPoints / 100} onChange={(event) => setPoLines((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, taxRateBasisPoints: Math.round(Number(event.target.value) * 100) } : item))} />
              {poLines.length > 1 ? <Button size="sm" variant="secondary" onClick={() => setPoLines((current) => current.filter((_, itemIndex) => itemIndex !== index))}>{ar ? "حذف" : "Remove"}</Button> : null}
            </Grid>)}</Stack>
          </Card>
        </Stack> : null}
      </Dialog>

      <Dialog open={Boolean(receiptEditor)} onClose={() => setReceiptEditor(null)} title={ar ? "استلام البضاعة" : "Receive goods"} footer={<Inline><Button variant="secondary" onClick={() => setReceiptEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="post-goods-receipt" onClick={() => void submitReceipt()}>{ar ? "ترحيل الاستلام" : "Post receipt"}</Button></Inline>}>
        {receiptEditor ? <Stack>
          <Grid columns={3}>
            <SelectField label={ar ? "المستودع" : "Warehouse"} value={receiptEditor.warehouseId} disabled={Boolean(receiptEditor.purchaseOrderId)} onChange={(event) => setReceiptEditor({ ...receiptEditor, warehouseId: event.target.value })}>{warehouses.filter((item) => item.isActive && item.branchId === context?.branchId).map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}</SelectField>
            <SelectField label={ar ? "المورد" : "Supplier"} value={receiptEditor.supplierId} disabled={Boolean(receiptEditor.purchaseOrderId)} onChange={(event) => setReceiptEditor({ ...receiptEditor, supplierId: event.target.value })}>{suppliers.filter((item) => item.status === "Active").map((item) => <option key={item.id} value={item.id}>{item.tradeName ?? item.legalName}</option>)}</SelectField>
            <Field label={ar ? "رقم فاتورة المورد" : "Supplier invoice"} value={receiptEditor.supplierInvoiceNumber} onChange={(event) => setReceiptEditor({ ...receiptEditor, supplierInvoiceNumber: event.target.value })} />
          </Grid>
          <Card title={ar ? "البنود المستلمة" : "Received lines"} actions={!receiptEditor.purchaseOrderId ? <Button size="sm" variant="secondary" onClick={() => setReceiptEditor({ ...receiptEditor, lines: [...receiptEditor.lines, newDraftLine()] })}>{ar ? "إضافة بند" : "Add line"}</Button> : undefined}>
            <Stack>{receiptEditor.lines.map((line, index) => <Grid columns={3} key={`${line.productVariantId}-${index}`}>
              <SelectField label={ar ? "الصنف" : "Item"} value={line.productVariantId} disabled={Boolean(receiptEditor.purchaseOrderId)} onChange={(event) => setReceiptEditor({ ...receiptEditor, lines: receiptEditor.lines.map((item, itemIndex) => itemIndex === index ? { ...item, productVariantId: event.target.value } : item) })}>{variants.map(({ product, variant }) => <option key={variant.id} value={variant.id}>{ar ? product.nameAr : product.nameEn} · {variant.sku}</option>)}</SelectField>
              <Field label={ar ? "الكمية" : "Quantity"} type="number" step="0.001" min="0.001" value={milliToQuantity(line.quantityMilli)} onChange={(event) => setReceiptEditor({ ...receiptEditor, lines: receiptEditor.lines.map((item, itemIndex) => itemIndex === index ? { ...item, quantityMilli: quantityToMilli(event.target.value) ?? 0 } : item) })} />
              <MoneyField data-testid={`goods-receipt-unit-cost-${index}`} label={ar ? "تكلفة الوحدة" : "Unit cost"} valueMinor={line.unitCostMinor} currency={suppliers.find((item) => item.id === receiptEditor.supplierId)?.currency ?? currency} onMinorChange={(value) => setReceiptEditor({ ...receiptEditor, lines: receiptEditor.lines.map((item, itemIndex) => itemIndex === index ? { ...item, unitCostMinor: value ?? 0 } : item) })} />
            </Grid>)}</Stack>
          </Card>
        </Stack> : null}
      </Dialog>
    </Stack>
  );
}
